import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { playNotificationSound, vibrate, showBrowserNotification, requestNotificationPermission } from '../utils/notifications';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useProviderJobs - Unified hook for all provider types to manage jobs
 * Works for: mechanics, roadside techs, admins, owners
 */
export function useProviderJobs() {
  const { user, getAuthHeaders } = useAuth();
  const { lastMessage, isConnected } = useWebSocket();
  
  const [jobOffers, setJobOffers] = useState([]);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [stats, setStats] = useState({ total_jobs: 0, total_earnings: 0, rating: 5.0 });
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  
  const soundEnabled = useRef(true);
  const lastOfferCount = useRef(0);
  
  // Check if user is a provider type
  const isProvider = user && (
    ['mechanic', 'roadside_tech', 'roadside_provider', 'admin'].includes(user.user_type) || 
    user.is_admin || 
    user.is_owner
  );
  
  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setLocation(loc);
          return loc;
        },
        (error) => console.warn('Location unavailable:', error.message),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
  }, []);
  
  // Update provider location on server
  const updateServerLocation = useCallback(async (loc) => {
    if (!isProvider || !loc) return;
    try {
      await axios.put(`${API}/mechanics/location`, { location: loc }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
    } catch (error) {
      console.error('Failed to update location:', error);
    }
  }, [isProvider, getAuthHeaders]);
  
  // Fetch all provider data
  const fetchData = useCallback(async () => {
    if (!isProvider) return;
    
    try {
      const [jobsRes, statsRes, offersRes] = await Promise.all([
        axios.get(`${API}/services`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/stats/mechanic`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/jobs/offers`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      const allJobs = jobsRes.data || [];
      const myUserId = user.user_id;
      
      // Filter jobs by status
      setAssignedJobs(allJobs.filter(j => j.mechanic_id === myUserId && ['accepted', 'assigned'].includes(j.status)));
      setActiveJobs(allJobs.filter(j => j.mechanic_id === myUserId && ['en_route', 'arrived', 'in_progress'].includes(j.status)));
      setCompletedJobs(allJobs.filter(j => j.mechanic_id === myUserId && j.status === 'completed'));
      
      setStats(statsRes.data);
      setIsOnline(user.is_online || false);
      
      // Handle new job offers with notifications
      const offers = offersRes.data || [];
      if (offers.length > lastOfferCount.current && soundEnabled.current) {
        // New offer arrived!
        playNotificationSound('newJob');
        vibrate('newJob');
        
        const newOffer = offers[0];
        showBrowserNotification('New Job Offer!', {
          body: `${newOffer.service_name} - $${newOffer.total_pay} - 5 min to accept!`,
          type: 'newJob',
          data: { type: 'job_offer', ...newOffer }
        });
      }
      lastOfferCount.current = offers.length;
      setJobOffers(offers);
      
    } catch (error) {
      console.error('Failed to fetch provider data:', error);
    } finally {
      setLoading(false);
    }
  }, [isProvider, getAuthHeaders, user]);
  
  // Toggle online/offline status
  const toggleOnlineStatus = useCallback(async (newStatus) => {
    if (!isProvider) return false;
    
    try {
      await axios.put(`${API}/mechanics/status`, { 
        is_online: newStatus, 
        location 
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setIsOnline(newStatus);
      return true;
    } catch (error) {
      console.error('Failed to update status:', error);
      return false;
    }
  }, [isProvider, getAuthHeaders, location]);
  
  // Accept a job offer
  const acceptOffer = useCallback(async (offerId) => {
    try {
      await axios.put(`${API}/jobs/offers/${offerId}/accept`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      playNotificationSound('jobAccepted');
      vibrate('jobAccepted');
      
      // Remove from offers, refresh data
      setJobOffers(prev => prev.filter(o => o.offer_id !== offerId));
      fetchData();
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to accept job' };
    }
  }, [getAuthHeaders, fetchData]);
  
  // Decline a job offer
  const declineOffer = useCallback(async (offerId, reason = 'Not available') => {
    try {
      await axios.put(`${API}/jobs/offers/${offerId}/decline`, { reason }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      setJobOffers(prev => prev.filter(o => o.offer_id !== offerId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to decline job' };
    }
  }, [getAuthHeaders]);
  
  // Update job status
  const updateJobStatus = useCallback(async (requestId, newStatus, notes = '') => {
    try {
      await axios.put(`${API}/services/${requestId}/status`, { 
        status: newStatus, 
        notes 
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      if (newStatus === 'completed') {
        playNotificationSound('payment');
        vibrate('payment');
      }
      
      fetchData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update status' };
    }
  }, [getAuthHeaders, fetchData]);
  
  // WebSocket handling for real-time updates
  useEffect(() => {
    if (!lastMessage || !isProvider) return;
    
    if (lastMessage.type === 'job_offer') {
      const offer = lastMessage.offer;
      setJobOffers(prev => {
        const exists = prev.find(o => o.offer_id === offer.offer_id);
        if (!exists) {
          if (soundEnabled.current) {
            playNotificationSound('newJob');
            vibrate('newJob');
          }
          return [offer, ...prev];
        }
        return prev;
      });
    } else if (lastMessage.type === 'request_status_update') {
      fetchData(); // Refresh all data on status changes
    }
  }, [lastMessage, isProvider, fetchData]);
  
  // Initial load and periodic refresh
  useEffect(() => {
    if (!isProvider) return;
    
    // Request notification permission on mount
    requestNotificationPermission();
    
    // Get initial location
    getCurrentLocation();
    
    // Initial fetch
    fetchData();
    
    // Refresh every 10 seconds
    const dataInterval = setInterval(fetchData, 10000);
    
    // Update location every 30 seconds
    const locationInterval = setInterval(() => {
      getCurrentLocation();
    }, 30000);
    
    return () => {
      clearInterval(dataInterval);
      clearInterval(locationInterval);
    };
  }, [isProvider, fetchData, getCurrentLocation]);
  
  // Update server location when local location changes
  useEffect(() => {
    if (location && isOnline) {
      updateServerLocation(location);
    }
  }, [location, isOnline, updateServerLocation]);
  
  return {
    // State
    jobOffers,
    assignedJobs,
    activeJobs,
    completedJobs,
    stats,
    isOnline,
    loading,
    location,
    isProvider,
    isConnected,
    
    // Actions
    toggleOnlineStatus,
    acceptOffer,
    declineOffer,
    updateJobStatus,
    refreshData: fetchData,
    getCurrentLocation,
    
    // Settings
    setSoundEnabled: (enabled) => { soundEnabled.current = enabled; }
  };
}

/**
 * useServiceWorker - Hook to manage service worker registration
 */
export function useServiceWorker() {
  const [registration, setRegistration] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((reg) => {
          console.log('Service Worker registered');
          setRegistration(reg);
          
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            newWorker?.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          });
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);
  
  const updateServiceWorker = useCallback(() => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }, [registration]);
  
  return { registration, updateAvailable, updateServiceWorker };
}

export default useProviderJobs;
